
import { Prop, Component, Watch, Vue } from "vue-property-decorator";
import Directions from "../utils/Directions";
import L, { ControlOptions } from "leaflet";
import * as esri from "esri-leaflet";
import { get } from "lodash";

class DirectionControl extends L.Control {
  private route!: number[][];
  private directions = new Directions();

  constructor(options: ControlOptions) {
    super(options);
  }

  public setRoute(route: number[][]): void {
    this.route = route;
  }

  public launchDirections(): void {
    const launchUrl = this.directions.getDirectionsUrl(
      this.route[0],
      this.route[this.route.length - 1]
    );
    window.open(launchUrl, "_blank");
  }

  onAdd() {
    const container: HTMLElement = L.DomUtil.create(
      "div",
      "leaflet-bar leaflet-control leaflet-control-layers"
    );
    L.DomUtil.create("i", "fas fa-2x fa-directions direction-icon", container);
    container.addEventListener("click", this.launchDirections.bind(this));
    return container;
  }
}

@Component({})
export default class MapTile extends Vue {
  @Prop({})
  route!: [number, number][];
  @Prop({})
  trackPath!: [number, number][];
  @Prop({})
  destination!: [number, number];
  @Prop({})
  otherTracks!: [number, number][][];
  @Prop()
  onScreen!: boolean;
  @Prop({})
  accessPoints!: [number, number][];
  @Prop({
    default: () => {
      return [];
    }
  })
  defibrillators!: [number, number][];

  public map!: L.Map;
  public selectedBaseMap!: string;
  public savedBaseMap!: string;
  public mapId = Date.now().toString();

  private directionControl = new DirectionControl({
    position: "topright"
  });

  private satelliteMap = esri.basemapLayer("Imagery", {
    maxZoom: 17
  });

  private streetMap = L.tileLayer(
    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 17
    }
  );

  private baseMaps = {
    Satellite: this.satelliteMap,
    Street: this.streetMap
  };

  private layerControl = L.control.layers(this.baseMaps);

  private getBoundingBox(points: [number, number][]): [number, number][] {
    const lats: number[] = [];
    const longs: number[] = [];

    points.forEach((point: [number, number]) => {
      lats.push(point[0]);
      longs.push(point[1]);
    });

    const minLat = Math.min.apply(null, lats);
    const maxLat = Math.max.apply(null, lats);
    const minLong = Math.min.apply(null, longs);
    const maxLong = Math.max.apply(null, longs);

    return [
      [minLat, minLong],
      [maxLat, maxLong]
    ];
  }

  private setHeight(): void {
    // set height to width of map div but maximum 500px
    const mapEl = document.getElementById(this.mapId) as HTMLDivElement;
    const h = Math.min(mapEl.offsetWidth, 500);
    mapEl.style.height = `${h}px`;
  }

  public invalidateSize(): void {
    this.setHeight();
    this.map.invalidateSize();
  }

  created() {
    window.addEventListener("resize", this.setHeight);
  }

  destroyed() {
    window.removeEventListener("resize", this.setHeight);
  }

  @Watch("route", {})
  routeChanged(newRoute: [number, number][]) {
    this.$nextTick(() => {
      console.log("newRoute", newRoute);
      if (!this.map) this.map = L.map(this.mapId);

      if (!this.onScreen) {
        this.map.on("load", () => {
          this.$emit("map-loaded");
        });
      }

      this.map.on("baselayerchange", (e: L.LayersControlEvent) => {
        this.selectedBaseMap =
          e.layer == this.baseMaps.Satellite ? "Satellite" : "Street";
        console.log("this.selectedBaseMap", this.selectedBaseMap);
      });

      // set map tile height from width
      this.setHeight();

      this.selectedBaseMap = "Satellite";

      if (this.onScreen) {
        this.layerControl.remove();
        this.layerControl.addTo(this.map);
      }

      this.satelliteMap.addTo(this.map);

      // Add other tracks
      if (get(this, "otherTracks.length") > 0) {
        this.otherTracks.forEach((track: [number, number][]) => {
          console.log("map.track", track);
          const trackLayer = L.polyline(track, {
            weight: 3,
            color: "rgb(198,191,28,1)",
            dashArray: "5"
          });
          trackLayer.addTo(this.map);
        });
      }

      // add access points
      if (get(this, "accessPoints.length") > 0) {
        this.accessPoints.forEach((point: [number, number]) => {
          const pointIcon = L.divIcon({
            className: "map-tile-icon-yellow",
            iconSize: [16, 0]
          });
          L.marker(point, { icon: pointIcon }).addTo(this.map);
        });
      }

      // add defibrillators
      if (get(this, "defibrillators.length") > 0) {
        this.defibrillators.forEach((point: [number, number]) => {
          const aedIcon = L.icon({
            iconUrl: require("@/static/aed.png"),
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          });
          L.marker(point, { icon: aedIcon }).addTo(this.map);
        });
      }

      if (newRoute && newRoute.length > 0) {
        const routeLayer = L.polyline(newRoute, {
          weight: 10,
          color: "rgba(102,157,246,1)",
          opacity: 1
        });
        routeLayer.addTo(this.map);
        this.map.fitBounds(routeLayer.getBounds());
      }

      if (get(this, "trackPath.length") > 0) {
        const trackLayer = L.polyline(this.trackPath, {
          weight: 3,
          color: "rgba(102,157,246,1)",
          dashArray: "5"
        });
        trackLayer.addTo(this.map);
      }

      // Destination Marker
      console.log("Destination", this.destination);
      const iconDest = L.divIcon({
        className: "map-tile-icon map-tile-icon-dest",
        iconSize: [16, 0]
      });
      L.marker(this.destination, { icon: iconDest }).addTo(this.map);

      // add the bounding box to the map, and set the map extent to it
      let bbox: [number, number][] = [];
      if (this.onScreen) {
        bbox = this.getBoundingBox(newRoute);
      } else {
        const coords = this.route.slice(this.route.length - 1);
        coords.push(this.destination);
        bbox = this.getBoundingBox(coords);
      }
      this.map.fitBounds(bbox);

      if (this.onScreen) {
        // add control to launch map directions URL or app
        this.directionControl.remove();
        this.directionControl.setRoute(newRoute);
        this.directionControl.addTo(this.map);
      }
    });
  }
}
